import React, { useEffect, useRef, useState } from 'react';
import './Sandbox.css';

function getSpeechRecognition() {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

export function Sandbox({
  runPrompt,
  onPromptChange,
  onResponse,
  botPrefillPrompt,
  botPrefillAndRun,
  initialPrompt = '',
  model = 'gpt-4o-mini',
  readOnly = false,
  height = '100%',
}) {
  const [prompt, setPrompt] = useState(initialPrompt);
  const [response, setResponse] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedModel, setSelectedModel] = useState(model);
  const [isListening, setIsListening] = useState(false);
  const [error, setError] = useState('');

  const recognitionRef = useRef(null);
  const latestRunIdRef = useRef(0);

  useEffect(() => {
    const SpeechRecognition = getSpeechRecognition();
    if (!SpeechRecognition || recognitionRef.current) return;
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';
    recognition.onstart = () => setIsListening(true);
    recognition.onend = () => setIsListening(false);
    recognition.onresult = (event) => {
      const transcript = event?.results?.[0]?.[0]?.transcript || '';
      if (transcript) setPrompt(transcript);
    };
    recognition.onerror = () => {
      setError('Voice recognition failed. Please type your prompt.');
      setIsListening(false);
    };
    recognitionRef.current = recognition;
  }, []);

  async function executeRun(promptText, modelName) {
    if (!promptText.trim() || loading) return;
    const runId = ++latestRunIdRef.current;
    setLoading(true);
    setError('');
    setResponse('');

    try {
      if (onPromptChange) onPromptChange(promptText);

      if (typeof runPrompt !== 'function') {
        throw new Error('Sandbox run function is not configured.');
      }

      const result = await runPrompt({ prompt: promptText, model: modelName });
      if (runId !== latestRunIdRef.current) return;

      const text = String(result?.response || result?.text || '');
      setResponse(text);
      if (onResponse) {
        onResponse({
          prompt: promptText,
          response: text,
          model: modelName,
          tokens: result?.tokens || null,
          latency: result?.latency || null,
        });
      }
    } catch (err) {
      if (runId !== latestRunIdRef.current) return;
      setError(err?.message || 'Sandbox run failed');
    } finally {
      if (runId === latestRunIdRef.current) setLoading(false);
    }
  }

  useEffect(() => {
    if (!botPrefillPrompt) return;
    setPrompt(botPrefillPrompt);
    if (botPrefillAndRun) {
      executeRun(botPrefillPrompt, selectedModel);
    }
    // Intentionally not depending on executeRun to avoid reruns caused by function identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [botPrefillPrompt, botPrefillAndRun, selectedModel]);

  const startVoiceInput = () => {
    const recognition = recognitionRef.current;
    if (!recognition) {
      setError('Voice recognition is not available in this browser.');
      return;
    }
    try {
      recognition.start();
    } catch (_err) {
      // start() can throw if called while already active.
    }
  };

  const clearAll = () => {
    setPrompt('');
    setResponse('');
    setError('');
  };

  const copyResponse = async () => {
    if (!response || !navigator.clipboard) return;
    await navigator.clipboard.writeText(response);
  };

  const hasVoiceSupport = !!getSpeechRecognition();

  return (
    <div className="sandbox" style={{ height }}>
      <div className="sandbox-header">
        <h3>AI Sandbox</h3>
        <div className="sandbox-controls">
          <select
            value={selectedModel}
            onChange={(e) => setSelectedModel(e.target.value)}
            disabled={loading || readOnly}
            aria-label="Sandbox model selector"
          >
            <option value="gpt-4o">GPT-4o</option>
            <option value="gpt-4o-mini">GPT-4o Mini</option>
          </select>
          {hasVoiceSupport && (
            <button
              className={`voice-btn ${isListening ? 'listening' : ''}`}
              onClick={startVoiceInput}
              disabled={loading || readOnly}
              aria-label="Start voice input"
              title="Start voice input"
            >
              Voice
            </button>
          )}
          <button onClick={clearAll} disabled={loading} aria-label="Clear sandbox" title="Clear sandbox">
            Clear
          </button>
        </div>
      </div>

      <div className="sandbox-prompt-area">
        <label htmlFor="sandbox-prompt">Your Prompt:</label>
        <textarea
          id="sandbox-prompt"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => {
            if (e.ctrlKey && e.key === 'Enter') executeRun(prompt, selectedModel);
          }}
          placeholder={isListening ? 'Listening...' : 'Type your prompt here. Ctrl+Enter to run.'}
          rows={3}
          disabled={loading || readOnly || isListening}
        />
        <button
          className="run-btn"
          onClick={() => executeRun(prompt, selectedModel)}
          disabled={loading || !prompt.trim() || readOnly}
        >
          {loading ? 'Running...' : 'Run'}
        </button>
      </div>

      {error && <div className="sandbox-error">{error}</div>}

      {response && (
        <div className="sandbox-response-area">
          <div className="response-header">
            <label>AI Response:</label>
            <button onClick={copyResponse} aria-label="Copy response" title="Copy response">
              Copy
            </button>
          </div>
          <div className="response-content">{response}</div>
        </div>
      )}

      <div className="sandbox-footer">
        <small>Tip: Add constraints such as audience, format, and boundaries for better specificity.</small>
      </div>
    </div>
  );
}
